// UploadField — drag-drop or click-to-pick file uploader for the admin.
//
// Two modes, switched by `mode`:
//
//   * mode="r2"     → posts to /api/admin/uploads/r2/upload-url, then PUTs
//                     the file directly to R2 with the returned headers.
//                     Reports progress via XHR upload events.
//
//   * mode="stream" → posts to /api/admin/uploads/stream/upload-url, then
//                     hands the file to the caller-supplied tus runner.
//                     The component doesn't import tus-js-client itself
//                     (keeps @bloom/ui browser-targeted and bundle-light);
//                     callers wire it in via `runTusUpload`.
//
// On success the component invokes `onUploaded({ url, key?, uid? })`:
//
//   * R2:     { url: publicUrl ?? signed-download-url, key }
//   * Stream: { uid }, plus an optional `meta` payload from runTusUpload
//             that callers can use to auto-fill durationSec from a poll.
//
// All admin labels are English-only (project convention).

import { useCallback, useRef, useState } from "react";
import { Loader2, Sparkles, Upload, X } from "lucide-react";
import { cn } from "../cn.ts";
import { Button } from "./button.tsx";

export interface UploadFieldR2Result {
  /** Public URL if the bucket has a public host; otherwise the signed GET URL. */
  url: string;
  key: string;
}

export interface UploadFieldStreamResult {
  /** Cloudflare Stream UID. */
  uid: string;
  /** Optional metadata from a post-upload poll (durationSec etc.). */
  meta?: Record<string, unknown>;
}

/**
 * Caller-supplied tus runner. The UploadField provides the
 * presigned tus endpoint URL + the picked file; the runner is
 * responsible for streaming bytes (typically via tus-js-client)
 * and resolving with the Stream UID once the upload completes.
 *
 * `onProgress` is forwarded to the runner so progress bar can
 * be updated from PATCH events.
 */
export type TusRunner = (args: {
  uploadUrl: string;
  uid: string;
  file: File;
  onProgress: (pct: number) => void;
}) => Promise<UploadFieldStreamResult>;

interface CommonProps {
  /** Short label shown above the dropzone, e.g. "Poster". */
  label: string;
  /** Optional hint underneath the dropzone. */
  hint?: string;
  /** Preview URL when a value is already set (existing record or freshly uploaded). */
  previewUrl?: string | null;
  /** Aspect ratio for the preview thumbnail. CSS shorthand, e.g. "9/16" or "16/9". */
  previewAspect?: string;
  /** Accept attribute hint — only filters the OS file dialog; server still validates. */
  accept?: string;
  /** Disable the entire field. */
  disabled?: boolean;
  /** Reset / "replace" handler — clears the preview so the user can pick again. */
  onReset?: () => void;
  /** Render a slot in the top-right corner of the dropzone (e.g. ✨ AI button). */
  rightAdornment?: React.ReactNode;
}

export type UploadFieldProps =
  | (CommonProps & {
      mode: "r2";
      kind: "poster" | "hero" | "still" | "keyart" | "card" | "social" | "thumbnail" | "subtitle";
      /** Called once the file is uploaded and the public URL is known. */
      onUploaded: (result: UploadFieldR2Result) => void;
      /** Auth-aware fetch wrapper (cookie-based) for hitting /api/admin/uploads/*. */
      apiFetch: typeof fetch;
    })
  | (CommonProps & {
      mode: "stream";
      /** Stream metadata.name — usually the episode title. */
      name: string;
      onUploaded: (result: UploadFieldStreamResult) => void;
      apiFetch: typeof fetch;
      runTus: TusRunner;
    });

interface R2UploadUrlResponse {
  uploadUrl: string;
  key: string;
  publicUrl: string | null;
  expiresInSec: number;
  requiredHeaders: Record<string, string>;
}

interface StreamUploadUrlResponse {
  uploadUrl: string;
  uid: string;
  expiresInSec: number;
}

interface DownloadUrlResponse {
  url: string;
  expiresInSec: number;
}

function bytesLabel(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)}GB`;
}

export function UploadField(props: UploadFieldProps) {
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setProgress(1);
      try {
        if (props.mode === "r2") {
          const signed = (await fetchJson<R2UploadUrlResponse>(
            props.apiFetch,
            "/api/admin/uploads/r2/upload-url",
            {
              method: "POST",
              body: JSON.stringify({
                kind: props.kind,
                contentType: file.type || guessType(file.name, props.kind),
                filename: file.name,
                sizeBytes: file.size,
              }),
            },
          )) as R2UploadUrlResponse;
          await putWithProgress(signed.uploadUrl, file, signed.requiredHeaders, (pct) =>
            setProgress(pct),
          );
          let publicUrl = signed.publicUrl;
          if (!publicUrl) {
            const dl = (await fetchJson<DownloadUrlResponse>(
              props.apiFetch,
              `/api/admin/uploads/r2/download-url?key=${encodeURIComponent(signed.key)}`,
              { method: "GET" },
            )) as DownloadUrlResponse;
            publicUrl = dl.url;
          }
          setProgress(100);
          props.onUploaded({ url: publicUrl, key: signed.key });
          // Reset progress after a beat so the bar fades; consumers control
          // the preview via `previewUrl`.
          setTimeout(() => setProgress(null), 500);
        } else {
          const signed = (await fetchJson<StreamUploadUrlResponse>(
            props.apiFetch,
            "/api/admin/uploads/stream/upload-url",
            {
              method: "POST",
              body: JSON.stringify({
                name: props.name || file.name,
                sizeBytes: file.size,
              }),
            },
          )) as StreamUploadUrlResponse;
          const result = await props.runTus({
            uploadUrl: signed.uploadUrl,
            uid: signed.uid,
            file,
            onProgress: (pct) => setProgress(pct),
          });
          setProgress(100);
          props.onUploaded(result);
          setTimeout(() => setProgress(null), 500);
        }
      } catch (err) {
        setError((err as Error).message || "Upload failed");
        setProgress(null);
      }
    },
    [props],
  );

  const onPick = () => inputRef.current?.click();
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    // Allow re-selecting the same file later.
    e.target.value = "";
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  const previewing = !!props.previewUrl && progress === null;
  const aspect = props.previewAspect ?? "9/16";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[var(--brand-ink)]">{props.label}</span>
        {props.rightAdornment}
      </div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          "relative overflow-hidden rounded-2xl border-2 border-dashed transition-colors",
          dragOver
            ? "border-[var(--brand-rose)] bg-[var(--brand-pink-soft)]/60"
            : "border-[var(--brand-border)] bg-white",
          props.disabled ? "pointer-events-none opacity-60" : "",
        )}
      >
        {previewing ? (
          <div className="relative">
            <div
              className="overflow-hidden bg-[var(--brand-pink-soft)]/30"
              style={{ aspectRatio: aspect }}
            >
              {isImage(props.previewUrl ?? "") ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={props.previewUrl ?? ""}
                  alt={props.label}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-4 text-center text-xs text-[var(--brand-muted)]">
                  <Sparkles className="h-5 w-5" aria-hidden />
                  <span className="font-mono break-all">{props.previewUrl}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 p-2">
              <span className="truncate text-[11px] text-[var(--brand-muted)]">Uploaded</span>
              <div className="flex gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={onPick}>
                  <Upload className="h-3.5 w-3.5" aria-hidden /> Replace
                </Button>
                {props.onReset ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={props.onReset}
                    aria-label="Remove upload"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onPick}
            className="flex w-full flex-col items-center justify-center gap-2 p-6 text-center"
            disabled={progress !== null}
          >
            {progress !== null ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-[var(--brand-rose)]" aria-hidden />
                <span className="text-xs text-[var(--brand-muted)]">
                  Uploading… {Math.round(progress)}%
                </span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 text-[var(--brand-rose)]" aria-hidden />
                <span className="text-sm font-medium text-[var(--brand-ink)]">
                  Drop file here or <span className="text-[var(--brand-rose)]">browse</span>
                </span>
                {props.hint ? (
                  <span className="text-[11px] text-[var(--brand-muted)]">{props.hint}</span>
                ) : null}
              </>
            )}
          </button>
        )}
        {/* Progress bar — thin rose strip pinned to the bottom edge. */}
        {progress !== null ? (
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-[var(--brand-pink-soft)]">
            <div
              className="h-full bg-[var(--brand-rose)] transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
        ) : null}
      </div>
      {error ? <p className="text-[11px] text-[var(--brand-rose)]">{error}</p> : null}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={props.accept}
        onChange={onInputChange}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// helpers
// ────────────────────────────────────────────────────────────────────────────

function isImage(url: string): boolean {
  // Match data URLs, common extensions, and Cloudflare images delivery URLs.
  if (url.startsWith("data:image/")) return true;
  return /\.(?:jpe?g|png|webp|avif|gif|svg)(?:$|\?)/i.test(url);
}

function guessType(filename: string, kind: string): string {
  if (kind === "subtitle") {
    return filename.toLowerCase().endsWith(".srt") ? "application/x-subrip" : "text/vtt";
  }
  const ext = filename.toLowerCase().split(".").pop() ?? "";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  if (ext === "avif") return "image/avif";
  return "application/octet-stream";
}

async function fetchJson<T>(apiFetch: typeof fetch, path: string, init: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...init.headers },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const msg =
      typeof body === "object" && body && "message" in body
        ? String((body as { message: unknown }).message)
        : res.statusText;
    throw new Error(msg);
  }
  return body as T;
}

/**
 * Browser PUT with XHR so we get upload-progress events. fetch() can't
 * report upload progress in any current browser, so XHR is the only
 * way to drive the progress bar honestly.
 */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) {
      // The browser refuses to set content-length itself — it's computed
      // from the body. The pre-signed URL was signed with the size, so
      // R2 still enforces the length without us setting the header.
      if (k.toLowerCase() === "content-length") continue;
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new Error(
            `R2 PUT failed (${xhr.status}): ${xhr.responseText.slice(0, 200) || "no body"}`,
          ),
        );
    };
    xhr.onerror = () => reject(new Error("R2 PUT network error"));
    xhr.send(file);
  });
}

export { bytesLabel };
